import OfferThankyouForm from '@/components/pages/create-webinar/thankyou/OfferThankyouForm';
import { opensInOfferPanel } from '@/lib/offerEmbed';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  deleteThankYouPageImage,
  fetchThankYouPage,
  listOfferTemplates,
  updateThankYouPage,
  uploadThankYouPageImage,
} from '@/features/webinar/webinarThunks';
import type { RootState } from '@/app/store';
import { Trash2 } from 'lucide-react';
import EvaluationThankYouForm from '@/components/pages/create-webinar/thankyou/EvaluationThankYouForm';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion2';
import PreviewThankyouPage from '@/components/pages/create-webinar/thankyou/PreviewThankyouPage';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';
import { mapHttpError } from '@/lib/mapApiError';
import type { AppError } from '@/features/auth/types';
import type { ThankYouPageInput } from '@/features/webinar/types';

interface ThankYouSectionProps {
  currentWebinarId?: number | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
}

type OfferMode = 'create' | 'existing';

export default function ThankYouSection({
  currentWebinarId,
  isEdit,
  onSaveSuccess,
}: ThankYouSectionProps) {
  const { t } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const {
    currentThankYouPage,
    thankYouPageLoading,
    currentWebinar,
    evaluationTemplate,
    offerTemplates,
  } = useAppSelector((state: RootState) => state.webinar);
  const isEvergreen = currentWebinar?.type === 'EVERGREEN';
  // L'évaluation n'est réellement active que si un template avec questions existe.
  const hasEvaluation = Boolean(evaluationTemplate?.questions?.length);
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarId = webinarIdParam || currentWebinarId;
  const visualIdentity = useAppSelector(
    (state: RootState) => state.workspace.currentWorkspace?.visualIdentity,
  );

  const [localState, setLocalState] = useState({
    displayLogo: false,
    displaySocialMedia: false,
    displaySpeakerProfile: false,
    displayOffer: false,
    displayImage: false,
    displayEvaluation: false,
  });

  // Textes personnalisés (titre, sous-titre, intro sondage). Vide = retour au défaut.
  const [localTexts, setLocalTexts] = useState({
    title: '',
    subtitle: '',
    evaluationIntroduction: '',
  });

  const [selectedThankYouPageImage, setSelectedThankYouPageImage] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewAccordionValue, setPreviewAccordionValue] = useState<string>(
    isEdit ? '' : isEvergreen ? '' : 'item-1',
  );
  const [offerMode, setOfferMode] = useState<OfferMode>('create');
  const [selectedExistingOfferId, setSelectedExistingOfferId] = useState<number | null>(null);
  const offerStateInitialized = useRef(false);

  const thankYouPageOfferId = useMemo(
    () => offerTemplates.find((offer) => offer.thankYouPageOffer)?.id ?? null,
    [offerTemplates],
  );

  const availableExistingOffers = useMemo(
    // Code embed et paiement Stripe ne s'affichent que dans le live, pas sur la page de remerciement.
    () =>
      offerTemplates.filter(
        (offer) => !offer.thankYouPageOffer && !offer.contents?.some(opensInOfferPanel),
      ),
    [offerTemplates],
  );

  const previewOfferId = useMemo(() => {
    if (!localState.displayOffer) return null;
    if (offerMode === 'existing') return selectedExistingOfferId;
    return thankYouPageOfferId;
  }, [localState.displayOffer, offerMode, selectedExistingOfferId, thankYouPageOfferId]);

  const buildThankYouPageInput = useCallback(
    (overrides?: {
      offerId?: number | null;
      displayOffer?: boolean;
      displayEvaluation?: boolean;
      offerMode?: OfferMode;
    }): ThankYouPageInput => {
      const effectiveOfferMode = overrides?.offerMode ?? offerMode;

      let offerId: number | null | undefined;
      if (overrides?.offerId !== undefined) {
        offerId = overrides.offerId;
      } else if (localState.displayOffer) {
        offerId = effectiveOfferMode === 'existing' ? selectedExistingOfferId : thankYouPageOfferId;
      }

      return {
        displayLogo: localState.displayLogo,
        displaySocialMedia: localState.displaySocialMedia,
        displaySpeakerProfile: localState.displaySpeakerProfile,
        displayOffer: overrides?.displayOffer ?? localState.displayOffer,
        displayImage: localState.displayImage,
        displayEvaluation:
          overrides?.displayEvaluation ?? (localState.displayEvaluation && hasEvaluation),
        offerId,
        offerConfigurationMode: effectiveOfferMode === 'existing' ? 'EXISTING_OFFER' : 'NEW_OFFER',
        title: localTexts.title.trim() || null,
        subtitle: localTexts.subtitle.trim() || null,
        evaluationIntroduction: localTexts.evaluationIntroduction.trim() || null,
      };
    },
    [
      localState,
      localTexts,
      hasEvaluation,
      offerMode,
      selectedExistingOfferId,
      thankYouPageOfferId,
    ],
  );

  const getWebinarIdNumber = useCallback(() => {
    if (!webinarId) return null;
    const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
    return isNaN(id) ? null : id;
  }, [webinarId]);

  const linkOfferToThankYouPage = useCallback(
    async (offerId: number, showToast = true, mode: OfferMode = offerMode) => {
      if (!workspaceId) return;
      const id = getWebinarIdNumber();
      if (id == null) return;

      await dispatch(
        updateThankYouPage({
          workspaceId,
          webinarId: id,
          input: buildThankYouPageInput({ offerId, displayOffer: true, offerMode: mode }),
        }),
      ).unwrap();

      if (showToast) {
        toast.success(t('postEventSection.offerMode.offerSavedSuccess'));
      }
    },
    [workspaceId, getWebinarIdNumber, dispatch, buildThankYouPageInput, t, offerMode],
  );

  const handleOfferSaved = useCallback(
    async (offerId: number) => {
      setOfferMode('create');
      setSelectedExistingOfferId(null);
      await linkOfferToThankYouPage(offerId, false, 'create');
    },
    [linkOfferToThankYouPage],
  );

  const handleOfferModeChange = useCallback(
    async (mode: OfferMode) => {
      setOfferMode(mode);

      if (mode === 'create') {
        setSelectedExistingOfferId(null);

        if (thankYouPageOfferId != null) {
          try {
            await linkOfferToThankYouPage(thankYouPageOfferId, false, 'create');
          } catch (error) {
            console.error('Error auto-linking thank you offer:', error);
            const appError = error as AppError | undefined;
            const errorMessage = appError?.message
              ? mapHttpError(appError.message)
              : 'errors.generic';
            toast.error(tCommon(errorMessage, 'Une erreur est survenue lors de la sauvegarde.'));
          }
        }
      }
    },
    [thankYouPageOfferId, linkOfferToThankYouPage, tCommon],
  );

  useEffect(() => {
    if (webinarId && workspaceId) {
      const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
      if (!isNaN(id)) {
        dispatch(fetchThankYouPage({ workspaceId, webinarId: id }));
        dispatch(listOfferTemplates({ workspaceId, webinarId: id, page: 0, size: 100 }));
      }
    }
  }, [webinarId, workspaceId, dispatch]);

  useEffect(() => {
    offerStateInitialized.current = false;
  }, [webinarId]);

  useEffect(() => {
    if (!currentThankYouPage || offerStateInitialized.current) return;

    if (!currentThankYouPage.displayOffer) {
      offerStateInitialized.current = true;
      return;
    }

    if (currentThankYouPage.offerConfigurationMode === 'EXISTING_OFFER') {
      setOfferMode('existing');
      setSelectedExistingOfferId(currentThankYouPage.offerId ?? null);
    } else {
      setOfferMode('create');
      setSelectedExistingOfferId(null);
    }
    offerStateInitialized.current = true;
  }, [currentThankYouPage]);

  // Le template d'évaluation arrive après la page : lu via une ref pour que son chargement ne
  // resynchronise pas toutes les cases sous l'utilisateur.
  const hasEvaluationRef = useRef(hasEvaluation);
  useEffect(() => {
    hasEvaluationRef.current = hasEvaluation;
  }, [hasEvaluation]);

  useEffect(() => {
    if (currentThankYouPage) {
      setLocalState({
        displayLogo: currentThankYouPage.displayLogo ?? false,
        displaySocialMedia: currentThankYouPage.displaySocialMedia ?? false,
        displaySpeakerProfile: currentThankYouPage.displaySpeakerProfile ?? false,
        displayOffer: currentThankYouPage.displayOffer ?? false,
        displayImage: currentThankYouPage.displayImage ?? false,
        // Éval ON seulement si le flag est actif ET qu'un template valide existe : le back
        // renvoie displayEvaluation=true par défaut sans contenu derrière, ce qui afficherait
        // l'éval comme active à tort à la création.
        displayEvaluation:
          (currentThankYouPage.displayEvaluation ?? false) && hasEvaluationRef.current,
      });
      setLocalTexts({
        title: currentThankYouPage.title ?? '',
        subtitle: currentThankYouPage.subtitle ?? '',
        evaluationIntroduction: currentThankYouPage.evaluationIntroduction ?? '',
      });
    }
  }, [currentThankYouPage]);

  // Quand le template finit par arriver, seule la case d'évaluation est relevée si le back
  // l'annonce active ; les autres cases gardent la saisie en cours.
  useEffect(() => {
    if (!hasEvaluation || !currentThankYouPage?.displayEvaluation) return;
    setLocalState((previous) =>
      previous.displayEvaluation ? previous : { ...previous, displayEvaluation: true },
    );
  }, [hasEvaluation, currentThankYouPage?.displayEvaluation]);

  useEffect(() => {
    if (isEdit) {
      setPreviewAccordionValue('');
    } else {
      setPreviewAccordionValue(isEvergreen ? '' : 'item-1');
    }
  }, [isEdit, isEvergreen]);

  const handleSwitchChange = useCallback(
    (
      field:
        | 'displayLogo'
        | 'displaySocialMedia'
        | 'displaySpeakerProfile'
        | 'displayOffer'
        | 'displayImage'
        | 'displayEvaluation',
      value: boolean,
    ) => {
      setLocalState((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!workspaceId || !webinarId) {
      return;
    }

    const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
    if (isNaN(id)) {
      return;
    }

    try {
      setIsSubmitting(true);

      if (localState.displayOffer && offerMode === 'existing' && selectedExistingOfferId == null) {
        toast.error(t('postEventSection.offerMode.selectOfferRequired'));
        setIsSubmitting(false);
        return;
      }

      if (localState.displayOffer && offerMode === 'create' && thankYouPageOfferId == null) {
        toast.error(t('postEventSection.offerMode.saveNewOfferFirst'));
        setIsSubmitting(false);
        return;
      }

      await dispatch(
        updateThankYouPage({
          workspaceId,
          webinarId: id,
          input: buildThankYouPageInput(),
        }),
      ).unwrap();

      if (selectedThankYouPageImage) {
        try {
          await dispatch(
            uploadThankYouPageImage({
              workspaceId,
              webinarId: id,
              file: selectedThankYouPageImage,
            }),
          ).unwrap();
        } catch (error) {
          console.error('Error uploading thank you page image:', error);
          const appError = error as AppError | undefined;
          if (appError?.message) {
            const mappedMessage = mapHttpError(appError.message);
            const translatedMessage = tCommon(mappedMessage, appError.message);
            toast.error(translatedMessage);
          } else {
            toast.error(
              tCommon(
                'errors.generic',
                "Une erreur est survenue lors du téléchargement de l'image.",
              ),
            );
          }
        }
      }

      toast.success(
        t('postEventSection.saveSuccess', 'Page de remerciement enregistrée avec succès'),
      );

      if (isEdit && onSaveSuccess) {
        window.scrollTo({ top: 0, behavior: 'smooth' });

        onSaveSuccess();
      } else {
        navigate(`/create-webinar/replay/${id}`);
      }
    } catch (error) {
      console.error('Error updating thank you page:', error);
      const appError = error as AppError | undefined;
      const errorMessage = appError?.message ? mapHttpError(appError.message) : 'errors.generic';
      const translatedMessage = tCommon(
        errorMessage,
        'Une erreur est survenue lors de la sauvegarde.',
      );
      toast.error(translatedMessage);

      if (appError?.details && Array.isArray(appError.details)) {
        appError.details.forEach((detail: { message?: string; fieldName?: string }) => {
          if (detail.message) {
            const mappedMessage = mapHttpError(detail.message);
            const translatedDetailMessage = tCommon(mappedMessage, detail.message);
            toast.error(translatedDetailMessage);
          }
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [
    workspaceId,
    webinarId,
    dispatch,
    localState,
    buildThankYouPageInput,
    offerMode,
    selectedExistingOfferId,
    thankYouPageOfferId,
    selectedThankYouPageImage,
    navigate,
    isEdit,
    onSaveSuccess,
    t,
    tCommon,
  ]);

  const syncThankYouPage = useCallback(async () => {
    if (!workspaceId || !webinarId) return;
    const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
    if (isNaN(id)) return;
    await dispatch(
      updateThankYouPage({
        workspaceId,
        webinarId: id,
        input: {
          ...buildThankYouPageInput(),
          displayEvaluation: localState.displayEvaluation,
        },
      }),
    ).unwrap();
  }, [workspaceId, webinarId, dispatch, localState.displayEvaluation, buildThankYouPageInput]);

  const handleThankYouPageImageDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!workspaceId || !webinarId) {
        return;
      }

      const id = typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10);
      if (isNaN(id)) {
        return;
      }

      if (selectedThankYouPageImage) {
        setSelectedThankYouPageImage(null);
        return;
      }

      if (currentThankYouPage?.imageUrl) {
        try {
          dispatch(
            deleteThankYouPageImage({
              workspaceId,
              webinarId: id,
            }),
          ).unwrap();
        } catch (error) {
          console.error('Error deleting thank you page image:', error);
        }
      }
    },
    [workspaceId, webinarId, dispatch, selectedThankYouPageImage, currentThankYouPage?.imageUrl],
  );

  if (thankYouPageLoading) {
    return (
      <div className="flex flex-col gap-4">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="flex items-center justify-between gap-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-6 w-11 rounded-full" />
          </div>
        ))}
        <Skeleton className="h-96 w-full rounded-[20px]" />
      </div>
    );
  }

  return (
    <TitleLayout
      title={t('postEventSection.title')}
      subtitle={t('postEventSection.subtitle')}
      isEdit={isEdit}
      description={t('postEventSection.description')}
    >
      <Accordion type="single" collapsible>
        <AccordionItem value="texts" className={isEdit ? 'accordion-page' : ''}>
          <AccordionTrigger>{t('postEventSection.texts.title')}</AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4 pt-2">
            <TextFieldWithLimit
              label={t('postEventSection.texts.pageTitleLabel')}
              className="bg-white"
              value={localTexts.title}
              placeholder={t('postEventSection.texts.pageTitlePlaceholder')}
              maxLength={255}
              onChange={(v) => setLocalTexts((prev) => ({ ...prev, title: v }))}
            />
            <TextFieldWithLimit
              label={t('postEventSection.texts.pageSubtitleLabel')}
              className="bg-white"
              value={localTexts.subtitle}
              placeholder={t('postEventSection.texts.pageSubtitlePlaceholder')}
              maxLength={500}
              onChange={(v) => setLocalTexts((prev) => ({ ...prev, subtitle: v }))}
            />
            <div className="flex flex-col gap-1">
              <TextFieldWithLimit
                label={t('postEventSection.texts.evaluationIntroLabel')}
                className="bg-white"
                value={localTexts.evaluationIntroduction}
                placeholder={t('postEventSection.texts.evaluationIntroPlaceholder')}
                multiline
                rows={3}
                maxLength={1000}
                onChange={(v) => setLocalTexts((prev) => ({ ...prev, evaluationIntroduction: v }))}
              />
              <p className="text-sm text-gray-tertiary px-1">
                {t('postEventSection.texts.evaluationIntroHint')}
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {visualIdentity?.logoUrl && (
        <div className="flex items-center justify-between gap-2">
          <Label>{t('postEventSection.displayLogo')}</Label>
          <Switch
            checked={localState.displayLogo}
            onCheckedChange={(checked) => handleSwitchChange('displayLogo', checked)}
          />
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <Label>{t('postEventSection.displaySocialMedia')}</Label>
        <Switch
          checked={localState.displaySocialMedia}
          onCheckedChange={(checked) => handleSwitchChange('displaySocialMedia', checked)}
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <Label>{t('postEventSection.displaySpeakerProfile')}</Label>
        <Switch
          checked={localState.displaySpeakerProfile}
          onCheckedChange={(checked) => handleSwitchChange('displaySpeakerProfile', checked)}
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <Label>{t('postEventSection.displayOffer')}</Label>
        <Switch
          checked={localState.displayOffer}
          onCheckedChange={(checked) => handleSwitchChange('displayOffer', checked)}
        />
      </div>

      <div className={localState.displayOffer ? 'flex flex-col gap-4' : 'hidden'}>
        <RadioGroup
          value={offerMode}
          onValueChange={(value) => void handleOfferModeChange(value as OfferMode)}
          className="flex items-center gap-4"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="create" id="offer-mode-create" />
            <Label htmlFor="offer-mode-create" className="font-normal cursor-pointer">
              {t('postEventSection.offerMode.createNew')}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="existing" id="offer-mode-existing" />
            <Label htmlFor="offer-mode-existing" className="font-normal cursor-pointer">
              {t('postEventSection.offerMode.selectExisting')}
            </Label>
          </div>
        </RadioGroup>

        {offerMode === 'create' ? (
          <OfferThankyouForm
            key={thankYouPageOfferId ?? 'new-offer'}
            displayOffer={localState.displayOffer}
            isEdit={isEdit}
            isEvergreen={isEvergreen}
            onOfferSaved={handleOfferSaved}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <Select
              value={selectedExistingOfferId != null ? String(selectedExistingOfferId) : undefined}
              onValueChange={(value) => setSelectedExistingOfferId(parseInt(value, 10))}
            >
              <SelectTrigger
                label={t('postEventSection.offerMode.selectOfferLabel')}
                className="bg-white"
              >
                <SelectValue placeholder={t('postEventSection.offerMode.selectOfferPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {availableExistingOffers.map((offer) => (
                  <SelectItem key={offer.id} value={String(offer.id)}>
                    {offer.privateTitle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableExistingOffers.length === 0 && (
              <p className="text-sm text-gray-tertiary px-1">
                {t('postEventSection.offerMode.noExistingOffers')}
              </p>
            )}
            {selectedExistingOfferId != null && (
              <OfferThankyouForm
                key={selectedExistingOfferId}
                prefillOffer={availableExistingOffers.find((o) => o.id === selectedExistingOfferId)}
                isExistingOfferMode
                displayOffer={localState.displayOffer}
                isEdit={isEdit}
                isEvergreen={isEvergreen}
                onOfferSaved={handleOfferSaved}
              />
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Label>{t('postEventSection.displayImage')}</Label>
        <Switch
          checked={localState.displayImage}
          onCheckedChange={(checked) => handleSwitchChange('displayImage', checked)}
        />
      </div>

      <div className="relative">
        <input
          type="file"
          id="thank-you-page-image-upload"
          className="sr-only"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setSelectedThankYouPageImage(file);
              if (!localState.displayImage) {
                setLocalState((prev) => ({ ...prev, displayImage: true }));
              }
            }
          }}
        />
        <Label
          htmlFor="thank-you-page-image-upload"
          className="p-4 bg-white rounded-2xl border border-medium flex items-center justify-between gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <div className="size-10 bg-medium rounded-lg flex items-center justify-center">
              <img src="/assets/attach.svg" alt={t('postEventSection.fileInput.attachIconAlt')} />
            </div>
            <div className="flex flex-col gap-2">
              <span>
                {selectedThankYouPageImage
                  ? selectedThankYouPageImage.name
                  : currentThankYouPage?.imageUrl
                    ? t('postEventSection.fileInput.existingFile')
                    : t('postEventSection.fileInput.selectFile')}
              </span>
              <p className="text-sm text-gray-tertiary">
                {selectedThankYouPageImage
                  ? `${(selectedThankYouPageImage.size / 1024 / 1024).toFixed(1)}mb`
                  : currentThankYouPage?.imageUrl
                    ? t('postEventSection.fileInput.existingFileDescription')
                    : t('postEventSection.fileInput.noFileSelected')}
              </p>
            </div>
          </div>
        </Label>
        {(selectedThankYouPageImage || currentThankYouPage?.imageUrl) && (
          <Trash2
            className="cursor-pointer absolute top-1/2 -translate-y-1/2 right-6"
            onClick={handleThankYouPageImageDelete}
          />
        )}
      </div>

      {localState.displayImage && (selectedThankYouPageImage || currentThankYouPage?.imageUrl) && (
        <div className="w-1/2 flex justify-center mx-auto">
          {selectedThankYouPageImage ? (
            <img
              src={URL.createObjectURL(selectedThankYouPageImage)}
              alt={t('postEventSection.previewImageAlt')}
              className="rounded-lg w-full h-auto object-contain "
            />
          ) : currentThankYouPage?.imageUrl ? (
            <img
              src={currentThankYouPage.imageUrl}
              alt={t('postEventSection.previewImageAlt')}
              className="rounded-lg w-1/2 flex h-auto object-contain"
            />
          ) : null}
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <Label>{t('postEventSection.displayEvaluation')}</Label>
        <Switch
          checked={localState.displayEvaluation}
          onCheckedChange={(checked) => handleSwitchChange('displayEvaluation', checked)}
        />
      </div>

      {workspaceId && webinarId && (
        <div className={localState.displayEvaluation ? '' : 'hidden'}>
          <EvaluationThankYouForm
            workspaceId={workspaceId}
            webinarId={typeof webinarId === 'number' ? webinarId : parseInt(webinarId, 10)}
            displayEvaluation={localState.displayEvaluation}
            onSyncThankYouPage={syncThankYouPage}
            isEvergreen={isEvergreen}
          />
        </div>
      )}

      <Accordion
        type="single"
        collapsible
        value={previewAccordionValue}
        onValueChange={setPreviewAccordionValue}
      >
        <AccordionItem value="item-1" className={isEdit ? 'accordion-page' : ''}>
          <AccordionTrigger>{t('postEventSection.preview')}</AccordionTrigger>
          <AccordionContent>
            <PreviewThankyouPage
              visualIdentity={visualIdentity || null}
              displayLogo={localState.displayLogo}
              displayEvaluation={localState.displayEvaluation}
              displayImage={localState.displayImage}
              imageUrl={
                selectedThankYouPageImage
                  ? URL.createObjectURL(selectedThankYouPageImage)
                  : (currentThankYouPage?.imageUrl ?? null)
              }
              displayOffer={localState.displayOffer}
              displaySocialMedia={localState.displaySocialMedia}
              displaySpeakerProfile={localState.displaySpeakerProfile}
              previewOfferId={previewOfferId}
              title={localTexts.title}
              subtitle={localTexts.subtitle}
              evaluationIntroduction={localTexts.evaluationIntroduction}
            />
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <Button
        className="w-full mt-6 disabled:bg-gray-200 disabled:text-gray-500 "
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting && <Loader />}
        {t('save')}
      </Button>
    </TitleLayout>
  );
}
